/* Add this JavaScript to your theme.js or create a new personalization.js file */

class ShopifyProductPersonalization {
    constructor() {
        this.isOpen = false;
        this.selectedFont = 'Roboto';
        this.customText = '';
        this.maxCharacters = 20;
        this.personalizationFee = 2000; // $20.00 in cents
        
        // Check if product has custom-text tag
        this.hasCustomText = window.productTags && window.productTags.includes('custom-text');
        
        if (this.hasCustomText) {
            this.initializeElements();
            this.bindEvents();
            this.updatePreview();
            this.observeFormChanges();
            // Initialize with no personalization properties
            this.updatePersonalizationFee();
        }
    }

    initializeElements() {
        this.container = document.getElementById('personalizationContainer');
        this.personalizationBtn = document.getElementById('personalizationBtn');
        this.personalizationPanel = document.getElementById('personalizationPanel');
        this.removeBtn = document.getElementById('removePersonalization');
        this.textInput = document.getElementById('customText');
        this.charCount = document.getElementById('charCount');
        this.previewText = document.getElementById('previewText');
        this.fontOptions = document.querySelectorAll('.font-option');
        this.fontInput = document.getElementById('selectedFontInput');
        this.feeInput = document.getElementById('personalizationFeeInput');
        
        // Find the product form (common Shopify selectors)
        this.productForm = document.querySelector('form[action*="/cart/add"]') || 
                          document.querySelector('.product-form') ||
                          document.querySelector('[data-product-form]') ||
                          document.querySelector('form.product-form');
                          
        // Find add to cart button
        this.addToCartBtn = document.querySelector('button[name="add"]') ||
                           document.querySelector('.btn-product-add') ||
                           document.querySelector('[data-add-to-cart]') ||
                           document.querySelector('input[type="submit"][value*="Add"]');
    }

    bindEvents() {
        if (!this.personalizationBtn) return;

        // Toggle personalization panel
        this.personalizationBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.togglePanel();
        });

        // Remove personalization
        if (this.removeBtn) {
            this.removeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.removePersonalization();
            });
        }

        // Text input events
        if (this.textInput) {
            this.textInput.addEventListener('input', (e) => {
                this.handleTextInput(e.target.value);
            });
        }

        // Font selection
        this.fontOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                e.preventDefault();
                this.selectFont(option);
            });
        });

        // Intercept form submission to validate personalization
        if (this.productForm) {
            this.productForm.addEventListener('submit', (e) => {
                this.handleFormSubmit(e);
            });
        }

        // Handle add to cart button clicks
        if (this.addToCartBtn) {
            this.addToCartBtn.addEventListener('click', (e) => {
                // Only prevent default if we need to show personalization warning
                if (this.shouldShowPersonalizationWarning()) {
                    e.preventDefault();
                    this.showPersonalizationPrompt();
                }
            });
        }
    }

    observeFormChanges() {
        // Watch for variant changes that might affect the form
        if (this.productForm) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' || mutation.type === 'attributes') {
                        this.updateFormState();
                    }
                });
            });

            observer.observe(this.productForm, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['data-variant-id', 'value']
            });
        }
    }

    togglePanel() {
        this.isOpen = !this.isOpen;
        
        if (this.isOpen) {
            this.personalizationPanel.classList.add('open');
            this.personalizationBtn.classList.add('active');
            this.personalizationBtn.innerHTML = '<span class="plus-icon">-</span> Close Personalization';
            
            // Focus on text input when opened
            setTimeout(() => {
                if (this.textInput) {
                    this.textInput.focus();
                }
            }, 100);
        } else {
            this.personalizationPanel.classList.remove('open');
            this.personalizationBtn.classList.remove('active');
            this.personalizationBtn.innerHTML = '<span class="plus-icon">+</span> Add Personalization';
        }

        // Update fee input based on whether personalization is active
        this.updatePersonalizationFee();

        // Trigger custom event for theme integration
        this.dispatchCustomEvent('personalization:toggle', {
            isOpen: this.isOpen,
            hasCustomization: !!this.customText
        });
    }

    removePersonalization() {
        this.customText = '';
        if (this.textInput) this.textInput.value = '';
        this.selectedFont = 'Roboto';
        
        // Reset font selection
        this.fontOptions.forEach(option => {
            option.classList.remove('selected');
            if (option.dataset.font === 'Roboto') {
                option.classList.add('selected');
            }
        });

        // Update hidden input
        if (this.fontInput) this.fontInput.value = 'Roboto';

        this.updateCharacterCount();
        this.updatePreview();
        this.updatePersonalizationFee(); // This will remove name attributes
        this.togglePanel();

        // Trigger custom event
        this.dispatchCustomEvent('personalization:removed');
    }

    updatePersonalizationFee() {
        const hasCustomText = this.customText.trim().length > 0;
        
        if (hasCustomText) {
            // Add name attributes to include in form submission
            if (this.textInput) {
                this.textInput.setAttribute('name', 'properties[Custom Text]');
            }
            if (this.fontInput) {
                this.fontInput.setAttribute('name', 'properties[Font]');
            }
            if (this.feeInput) {
                this.feeInput.setAttribute('name', 'properties[_Personalization Fee]');
                this.feeInput.value = '$20.00';
            }
        } else {
            // Remove name attributes to exclude from form submission
            if (this.textInput) {
                this.textInput.removeAttribute('name');
            }
            if (this.fontInput) {
                this.fontInput.removeAttribute('name');
            }
            if (this.feeInput) {
                this.feeInput.removeAttribute('name');
                this.feeInput.value = '';
            }
        }
    }

    handleTextInput(value) {
        if (value.length <= this.maxCharacters) {
            this.customText = value;
            this.updateCharacterCount();
            this.updatePreview();
            this.updatePersonalizationFee();

            // Trigger custom event
            this.dispatchCustomEvent('personalization:textChanged', {
                text: this.customText,
                length: this.customText.length
            });
        }
    }

    updateCharacterCount() {
        if (!this.charCount) return;
        
        const count = this.customText.length;
        this.charCount.textContent = `${count}/${this.maxCharacters}`;
        
        // Change color when approaching limit
        if (count >= 18) {
            this.charCount.style.color = '#f44336';
        } else if (count >= 15) {
            this.charCount.style.color = '#ff9800';
        } else {
            this.charCount.style.color = '#666';
        }
    }

    selectFont(fontElement) {
        // Remove selected class from all fonts
        this.fontOptions.forEach(option => {
            option.classList.remove('selected');
        });

        // Add selected class to clicked font
        fontElement.classList.add('selected');
        this.selectedFont = fontElement.dataset.font;
        
        // Update hidden input
        if (this.fontInput) {
            this.fontInput.value = this.selectedFont;
        }
        
        this.updatePreview();

        // Trigger custom event
        this.dispatchCustomEvent('personalization:fontChanged', {
            font: this.selectedFont
        });
    }

    updatePreview() {
        if (!this.previewText) return;
        
        const text = this.customText || 'Enter text to see preview';
        this.previewText.textContent = text;
        this.previewText.style.fontFamily = this.getFontFamily(this.selectedFont);
    }

    getFontFamily(fontName) {
        const fontMap = {
            'Roboto': "'Roboto', sans-serif",
            'Iceland': '"Iceland", sans-serif'
        };
        return fontMap[fontName] || "'Roboto', sans-serif";
    }

    shouldShowPersonalizationWarning() {
        // Show warning if panel is open but no text is entered
        return this.isOpen && !this.customText.trim();
    }

    showPersonalizationPrompt() {
        const proceed = confirm('You have personalization open but no custom text entered. Do you want to add the product to cart without personalization?');
        if (proceed) {
            this.removePersonalization();
            // Submit the form after removing personalization
            setTimeout(() => {
                if (this.productForm) {
                    this.productForm.submit();
                }
            }, 100);
        }
    }

    handleFormSubmit(e) {
        // Validate personalization before submission
        if (this.isOpen && this.customText.trim()) {
            // Ensure hidden inputs are properly set
            if (this.fontInput) {
                this.fontInput.value = this.selectedFont;
            }

            // Add personalization data to form data
            this.addPersonalizationToForm();
            
            // Handle the fee addition
            this.handlePersonalizationFee(e);
        }

        // Trigger custom event
        this.dispatchCustomEvent('personalization:formSubmit', {
            hasPersonalization: !!this.customText.trim(),
            personalizationData: this.getPersonalizationData()
        });
    }

    handlePersonalizationFee(e) {
        const hasPersonalization = this.customText.trim().length > 0;
        
        if (hasPersonalization) {
            // Prevent the default form submission
            e.preventDefault();
            
            // Add main product first, then add fee
            this.addMainProductThenFee();
        }
    }

    addMainProductThenFee() {
        if (!this.productForm) return;

        // Get form data
        const formData = new FormData(this.productForm);
        
        // Add main product to cart
        fetch('/cart/add.js', {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            console.log('Main product added:', data);
            
            // Add personalization fee as a separate line item
            return this.addPersonalizationFeeToCart();
        })
        .then(() => {
            // Redirect to cart or show success message
            this.showSuccessMessage();
        })
        .catch(error => {
            console.error('Error adding to cart:', error);
            this.showErrorMessage();
        });
    }

    addPersonalizationFeeToCart() {
        // You have two options here:
        // Option 1: Create a "Personalization Fee" product in your Shopify admin and use its variant ID
        // Option 2: Use a line item property (current approach)
        
        // For now, we'll use a dummy product approach
        // You need to create a "Personalization Fee" product in Shopify admin
        // and get its variant ID, then replace 'YOUR_PERSONALIZATION_FEE_VARIANT_ID' below
        
        const feeData = {
            id: 'YOUR_PERSONALIZATION_FEE_VARIANT_ID', // Replace with actual variant ID
            quantity: 1,
            properties: {
                '_Personalization Fee': 'Custom Text Service',
                '_Hidden': 'true' // Hide from customer view if needed
            }
        };

        return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(feeData)
        });
    }

    showSuccessMessage() {
        // You can customize this to show a cart drawer, redirect, or show a message
        alert('Product with personalization added to cart successfully!');
        
        // Optional: Redirect to cart
        // window.location.href = '/cart';
        
        // Optional: Trigger cart drawer if your theme has one
        if (window.theme && window.theme.cart && window.theme.cart.open) {
            window.theme.cart.open();
        }
    }

    showErrorMessage() {
        alert('There was an error adding the product to cart. Please try again.');
    }

    addPersonalizationToForm() {
        if (!this.productForm || !this.customText.trim()) return;

        // Ensure personalization inputs have the correct name attributes
        this.updatePersonalizationFee();

        // Double-check that inputs have proper values
        if (this.textInput && this.customText.trim()) {
            this.textInput.value = this.customText.trim();
        }

        if (this.fontInput) {
            this.fontInput.value = this.selectedFont;
        }
    }

    updateFormState() {
        // Called when form state changes (e.g., variant selection)
        // You can add logic here to handle variant-specific personalization
    }

    getPersonalizationData() {
        if (!this.customText.trim()) return null;
        
        return {
            text: this.customText.trim(),
            font: this.selectedFont,
            fontFamily: this.getFontFamily(this.selectedFont)
        };
    }

    dispatchCustomEvent(eventName, data = {}) {
        const event = new CustomEvent(eventName, {
            detail: {
                ...data,
                personalization: this
            },
            bubbles: true
        });
        document.dispatchEvent(event);
    }

    // Public methods for external access
    getText() {
        return this.customText;
    }

    getFont() {
        return this.selectedFont;
    }

    isPersonalizationActive() {
        return this.isOpen && !!this.customText.trim();
    }

    // Method to programmatically set personalization
    setPersonalization(text, font = 'Roboto') {
        if (text && text.length <= this.maxCharacters) {
            this.customText = text;
            this.selectedFont = font;
            
            if (this.textInput) this.textInput.value = text;
            if (this.fontInput) this.fontInput.value = font;
            
            // Update font selection
            this.fontOptions.forEach(option => {
                option.classList.remove('selected');
                if (option.dataset.font === font) {
                    option.classList.add('selected');
                }
            });
            
            this.updateCharacterCount();
            this.updatePreview();
            this.updatePersonalizationFee();
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Initialize personalization
    window.shopifyPersonalization = new ShopifyProductPersonalization();
    
    // Expose for debugging and external access
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.debug) {
        console.log('Shopify Product Personalization initialized', window.shopifyPersonalization);
    }
});

// Also initialize on AJAX page loads (for single-page app themes)
document.addEventListener('shopify:section:load', function() {
    window.shopifyPersonalization = new ShopifyProductPersonalization();
});

// Handle theme section reloads
document.addEventListener('shopify:section:reloaded', function() {
    window.shopifyPersonalization = new ShopifyProductPersonalization();
});

// Expose global methods for theme integration
window.ShopifyPersonalization = {
    getInstance: function() {
        return window.shopifyPersonalization;
    },
    
    getPersonalizationData: function() {
        return window.shopifyPersonalization ? window.shopifyPersonalization.getPersonalizationData() : null;
    },
    
    setPersonalization: function(text, font) {
        if (window.shopifyPersonalization) {
            window.shopifyPersonalization.setPersonalization(text, font);
        }
    }
};